import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider/LocalizationProvider";
import { Box, Typography } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useGetLocale } from "@refinedev/core";
import { trTR } from "@mui/x-date-pickers/locales/trTR";
import { enUS } from "@mui/x-date-pickers/locales/enUS";
import "dayjs/locale/tr";
import { useState } from "react";
import { useTranslation } from "../../../hooks/useTranslation";
import { useFiltersWithContext } from "../filter-context/useFiltersWithContext";

type DateRangeFilterProps = {
  name: string;
  labelKey: string;
};

export const DateRangeFilter = (props: DateRangeFilterProps) => {
  const { name, labelKey } = props;

  const getLocale = useGetLocale();
  const locale = getLocale();

  const { getFilterValue, setFilter, deleteFilter } = useFiltersWithContext();
  const { translate } = useTranslation();

  const [error, setError] = useState<boolean>(false);

  const handleDateChange = (value: Dayjs | null, operator: "gte" | "lte") => {
    const setValidationError = (condition: boolean) => setError(condition);

    if (value) {
      const selectedDate = value.toDate();
      const currentStartDate = getValue(name, "gte");
      const currentEndDate = getValue(name, "lte");

      const isGreaterThanEndDate =
        operator === "gte" && currentEndDate && value.isAfter(currentEndDate);
      const isLessThanStartDate =
        operator === "lte" &&
        currentStartDate &&
        value.isBefore(currentStartDate);

      if (isGreaterThanEndDate || isLessThanStartDate) {
        setValidationError(true);
        return;
      } else {
        setValidationError(false);
      }

      setFilter({
        field: name,
        value: value,
        labelInfo: {
          descriptionKey: labelKey,
          value: selectedDate.toISOString(),
          isValueDate: true,
        },
        operator: operator,
      });
    } else {
      deleteFilter(name, operator);
      setValidationError(false);
    }
  };

  const getValue = (name: string, operator: "gte" | "lte") => {
    const filter = getFilterValue(name, operator);
    if (filter) {
      return dayjs(filter);
    }
    return null;
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={locale}
      localeText={
        (locale === "tr" ? trTR : enUS).components.MuiLocalizationProvider
          .defaultProps.localeText
      }
    >
      <Box display="flex" flexDirection="row" gap={2} alignItems="center">
        <Box flex={1}>
          <Typography>{translate(labelKey)}</Typography>
          <Typography variant="body2" color="error">
            {error && translate("common.errors.endDateBeforeStartDate")}
          </Typography>
        </Box>
        <DatePicker
          label={translate("filters.placeholders.start")}
          onChange={(val) => handleDateChange(val, "gte")}
          value={getValue(name, "gte")}
          slotProps={{
            textField: {
              size: "small",
              sx: { width: 200 },
              error: Boolean(error),
            },
            field: {
              clearable: true,
              onClear: () => {
                deleteFilter(name, "gte");
                setError(false);
              },
            },
          }}
        />
        <DatePicker
          label={translate("filters.placeholders.end")}
          onChange={(val) => handleDateChange(val, "lte")}
          value={getValue(name, "lte")}
          slotProps={{
            textField: {
              size: "small",
              sx: { width: 200 },
              error: Boolean(error),
            },
            field: {
              clearable: true,
              onClear: () => {
                deleteFilter(name, "lte");
                setError(false);
              },
            },
          }}
        />
      </Box>
    </LocalizationProvider>
  );
};
